import * as React from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { es } from "date-fns/locale";
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  ChevronsLeftIcon,
  ChevronsRightIcon,
} from "lucide-react";

import { cn } from "lib/utils";
import { Button, buttonVariants } from "js/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "js/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "js/components/ui/select";

interface DatePickerProps {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  disabled?: boolean;
  placeholder?: string;
  minDate?: Date;
  maxDate?: Date;
  fromYear?: number;
  toYear?: number;
  defaultMonth?: Date;
  className?: string;
  hasError?: boolean;
  formatDisplay?: (date: Date) => string;
}

const MONTHS = [
  "Enero",
  "Febrero",
  "Marzo",
  "Abril",
  "Mayo",
  "Junio",
  "Julio",
  "Agosto",
  "Septiembre",
  "Octubre",
  "Noviembre",
  "Diciembre",
];

function DatePicker({
  value,
  onChange,
  disabled = false,
  placeholder = "DD/MM/YYYY",
  minDate = new Date("1900-01-01"),
  maxDate = new Date(),
  fromYear = 1900,
  toYear = new Date().getFullYear(),
  defaultMonth = new Date(2000, 0),
  className,
  hasError = false,
  formatDisplay,
}: DatePickerProps) {
  const [open, setOpen] = React.useState(false);
  const [currentMonth, setCurrentMonth] = React.useState<Date>(
    value || defaultMonth
  );

  // Generate years array for dropdown
  const years = React.useMemo(() => {
    const yearsArray: number[] = [];
    for (let year = toYear; year >= fromYear; year--) {
      yearsArray.push(year);
    }
    return yearsArray;
  }, [fromYear, toYear]);

  const handleMonthChange = (monthIndex: string) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(parseInt(monthIndex));
    setCurrentMonth(newDate);
  };

  const handleYearChange = (year: string) => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(parseInt(year));
    setCurrentMonth(newDate);
  };

  const handlePreviousDecade = () => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(currentMonth.getFullYear() - 10);
    if (newDate.getFullYear() >= fromYear) {
      setCurrentMonth(newDate);
    } else {
      newDate.setFullYear(fromYear);
      setCurrentMonth(newDate);
    }
  };

  const handleNextDecade = () => {
    const newDate = new Date(currentMonth);
    newDate.setFullYear(currentMonth.getFullYear() + 10);
    if (newDate.getFullYear() <= toYear) {
      setCurrentMonth(newDate);
    } else {
      newDate.setFullYear(toYear);
      setCurrentMonth(newDate);
    }
  };

  const handlePreviousMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() - 1);
    setCurrentMonth(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(currentMonth.getMonth() + 1);
    setCurrentMonth(newDate);
  };

  const handleDateSelect = (date: Date | undefined) => {
    onChange?.(date);
    if (date) {
      setOpen(false);
    }
  };

  const displayValue = React.useMemo(() => {
    if (!value) return "";
    if (formatDisplay) return formatDisplay(value);
    return value.toLocaleDateString("es-MX", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  }, [value, formatDisplay]);

  const defaultClassNames = getDefaultClassNames();

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal border-gray-200",
            !value && "text-muted-foreground",
            hasError && "border-red-500",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <div className="p-3">
          {/* Navigation Header */}
          <div className="flex items-center justify-between gap-1 mb-3">
            {/* Decade navigation */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handlePreviousDecade}
              disabled={currentMonth.getFullYear() <= fromYear}
            >
              <ChevronsLeftIcon className="h-4 w-4" />
            </Button>

            {/* Month navigation */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handlePreviousMonth}
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </Button>

            {/* Month/Year Selects */}
            <div className="flex items-center gap-1 flex-1 justify-center">
              <Select
                value={currentMonth.getMonth().toString()}
                onValueChange={handleMonthChange}
              >
                <SelectTrigger className="h-8 w-[110px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month, index) => (
                    <SelectItem key={month} value={index.toString()}>
                      {month}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select
                value={currentMonth.getFullYear().toString()}
                onValueChange={handleYearChange}
              >
                <SelectTrigger className="h-8 w-[85px] text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[200px]">
                  {years.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Month navigation */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleNextMonth}
            >
              <ChevronRightIcon className="h-4 w-4" />
            </Button>

            {/* Decade navigation */}
            <Button
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              onClick={handleNextDecade}
              disabled={currentMonth.getFullYear() >= toYear}
            >
              <ChevronsRightIcon className="h-4 w-4" />
            </Button>
          </div>

          {/* Calendar Grid */}
          <DayPicker
            mode="single"
            selected={value}
            onSelect={handleDateSelect}
            month={currentMonth}
            onMonthChange={setCurrentMonth}
            disabled={(date) => date > maxDate || date < minDate}
            locale={es}
            showOutsideDays
            fixedWeeks
            hideNavigation
            classNames={{
              root: cn("w-full", defaultClassNames.root),
              months: cn("flex flex-col", defaultClassNames.months),
              month: cn("flex flex-col w-full", defaultClassNames.month),
              month_caption: cn("hidden", defaultClassNames.month_caption),
              table: "w-full border-collapse",
              weekdays: cn("flex justify-between", defaultClassNames.weekdays),
              weekday: cn(
                "text-muted-foreground flex-1 font-normal text-[0.8rem] select-none text-center",
                defaultClassNames.weekday
              ),
              week: cn("flex w-full justify-between mt-1", defaultClassNames.week),
              day: cn(
                "relative p-0 text-center select-none flex-1",
                defaultClassNames.day
              ),
              day_button: cn(
                buttonVariants({ variant: "ghost" }),
                "h-8 w-full p-0 font-normal aria-selected:opacity-100"
              ),
              selected: cn(
                "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground rounded-md",
                defaultClassNames.selected
              ),
              today: cn(
                "bg-accent text-accent-foreground rounded-md",
                defaultClassNames.today
              ),
              outside: cn(
                "text-muted-foreground opacity-50",
                defaultClassNames.outside
              ),
              disabled: cn(
                "text-muted-foreground opacity-50",
                defaultClassNames.disabled
              ),
              hidden: cn("invisible", defaultClassNames.hidden),
            }}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

export { DatePicker };
